<template lang="pug">
template(v-if="showTopBar")
  header.mobile-topbar
    button.mobile-topbar__toggle(
      type="button"
      :aria-label="isMobileMenuOpen ? 'Close menu' : 'Open menu'"
      :aria-expanded="isMobileMenuOpen"
      @click="toggleMenu"
    )
      fai(type="fal" icon="bars").size-24
    router-link.mobile-topbar__logo(to="/")
      img(src="@/assets/images/logos/bolt-rounded.png").size-50px
      img(src="@/assets/images/logos/logo-text@2x.png").w-170.h-30
  .mobile-topbar__backdrop(
    v-if="isMobileMenuOpen"
    @click="closeMenu"
  )
</template>

<script lang="ts">
import {
  defineComponent, computed, watch, onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { isWebAppPath } from '@/utils/webAppRoutes';

// Top bar + backdrop for the CMS sidebar drawer. Only visible below 768px (see style).
export default defineComponent({
  name: 'AppMobileTopBar',
  setup() {
    const store = useStore();
    const route = useRoute();

    const showTopBar = computed(() => !isWebAppPath(route.path)
      && store.getters['users/isMeSignedIn']
      && (store.getters['users/isAdmin'] || store.getters['users/isOrgAdmin']));
    const isMobileMenuOpen = computed(() => store.getters['shared/isMobileMenuOpen']);

    const setOpen = (open: boolean) => store.dispatch('shared/setMobileMenuOpen', open);
    const toggleMenu = () => setOpen(!isMobileMenuOpen.value);
    const closeMenu = () => setOpen(false);

    // Lets content styles reserve room for the fixed top bar
    watch(showTopBar, (show) => {
      document.body.classList.toggle('has-mobile-topbar', show);
    }, { immediate: true });
    // Lock page scroll behind the open drawer
    watch(isMobileMenuOpen, (open) => {
      document.body.classList.toggle('mobile-menu-open', open);
    }, { immediate: true });
    onBeforeUnmount(() => {
      document.body.classList.remove('has-mobile-topbar', 'mobile-menu-open');
    });

    return {
      showTopBar, isMobileMenuOpen, toggleMenu, closeMenu,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

.mobile-topbar,
.mobile-topbar__backdrop {
  display: none;
}

@media screen and (max-width: 768px) {
  .mobile-topbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: $mobile-topbar-height;
    padding: 0 0.5rem;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    z-index: 998;

    &__toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border: 0;
      background: transparent;
      color: #303133;
    }

    &__logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      img:first-child {
        width: 32px;
        height: 32px;
      }

      img:last-child {
        width: 120px;
        height: auto;
      }
    }
  }

  .mobile-topbar__backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.45);
    z-index: 999;
  }
}
</style>
